import { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { classNames } from 'primereact/utils';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Password } from 'primereact/password';
import { Dropdown } from 'primereact/dropdown';
import { Divider } from 'primereact/divider';
import { Col, Row } from 'react-bootstrap';
import { InputSwitch } from 'primereact/inputswitch';

// props:
// - visible
// - user
// - onHide
// - onUserSaved
function UserEditDialog(props) {

  // #region vars and consts
  
  const [isNew, setIsNew] = useState(false);    
  const [profiles, setProfiles] = useState(false);
  const [active, setActive] = useState(true);
  const defaultValues = {
    userName: "",
    pass: "",
    firstName: "",
    lastName: "",
    profile: null,
    cellPhone: ""
  };
  const {
    control,
    formState: { errors },
    handleSubmit,
    reset
  } = useForm({ defaultValues });

  // #endregion

  // #region methods

  const resetForm = () => {
    reset(defaultValues);    
  }

  const getProfiles = () => {    
    fetch(`${process.env.REACT_APP_API_URL}/profile/findAll`)
      .then(response => response.json())
      .then(data => {
        if (data.responseCode === 'OK') {
          setProfiles(data.responseObject);
        } else {
          console.warn('ProfilesDataTable: getProfiles: error: data:', data);
        }        
      });
  };  

  const createUser = (
    userName,
    pass,
    firstName,
    lastName,
    profileId,
    cellPhone,
    active) => {
    fetch(`${process.env.REACT_APP_API_URL}/user/create`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        userName,
        pass,
        firstName,
        lastName,
        profileId,
        cellPhone,
        active        
      }),
    }).then(response => response.json()).then(data => {
      if (data.responseCode === 'OK') {
        console.log("UserEditDialog: createUser: success");
        props.onUserSaved();
        reset();
      } else {
        console.log("UserEditDialog: createUser: error");
      }
    });
  };

  const updateUser = (
    userId,
    userName,
    pass,
    firstName,
    lastName,
    profileId,
    cellPhone,
    active) => {
    fetch(`${process.env.REACT_APP_API_URL}/user/update`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        userId,
        userName,
        pass,
        firstName,
        lastName,
        profileId,
        cellPhone,
        active        
      }),
    }).then(response => response.json()).then(data => {
      if (data.responseCode === 'OK') {
        console.log("UserEditDialog: updateUser: success");
        props.onUserSaved();
        reset();
      } else {
        console.log("UserEditDialog: updateUser: error");
      }
    });
  };

  const getFormErrorMessage = (name) => {
    return errors[name] && (
      <small className="p-error">
        {errors[name].message}
      </small>
    )
  };

  // #endregion

  // #region events

  const onDialogHide = () => {
    if (props.onHide) props.onHide();
  };

  const onFormSubmit = (data) => {
    if (isNew) {
      createUser(
        data.userName,
        data.pass,
        data.firstName,
        data.lastName,
        data.profile.profileId,
        data.cellPhone,
        active);
    } else {
      updateUser(
        props.user.userId,
        data.userName,
        data.pass,
        data.firstName,
        data.lastName,
        data.profile.profileId,
        data.cellPhone,
        active);
    }
  };

  // #endregion

  // #region templates

  const passwordFieldHeaderTemplate = <h6>Ingresa la contraseña</h6>;

  const passwordFieldFooterTemplate = (
    <>
      <Divider />
      <h6>
        Sugerencias
      </h6>
      <ul>
        <li>Al menos una minúscula</li>
        <li>Al menos una mayúscula</li>
        <li>Al menos un número</li>
        <li>Mínimo 8 caracteres</li>
      </ul>
    </>
  );

  const formTemplate = (
    <>
      <form
        onSubmit={handleSubmit(onFormSubmit)}
        className="p-fluid">
        <Row>
          <Col className='pt-1'>
            <label
              htmlFor="userName"
              className={classNames({ 'p-error': errors.userName })}>
              Usuario*
            </label>
            <Controller
              name="userName"
              control={control}
              rules={{ required: 'El usuario es requerido.', pattern: { value: /^[A-Z0-9\.]{2,255}$/i, message: 'Usuario no válido' } }}
              render={({ field, fieldState }) => (
                <InputText
                  id={field.name}
                  {...field}
                  maxLength={255}
                  autoFocus
                  className={classNames({ 'p-invalid': fieldState.invalid })} />
              )} />
            {getFormErrorMessage('userName')}
          </Col>
        </Row>
        <Row>
          <Col className='pt-1'>
            <label
              htmlFor="pass"
              className={classNames({ 'p-error': errors.pass })}>
              Contraseña*
            </label>
            <Controller
              name="pass"
              control={control}
              rules={{ required: 'La contraseña es requerida.' }}
              render={({ field, fieldState }) => (
                <Password
                  id={field.name}
                  {...field}
                  maxLength={255}
                  minLength={8}
                  toggleMask
                  autoFocus
                  className={classNames({ 'p-invalid': fieldState.invalid })}
                  header={passwordFieldHeaderTemplate}
                  footer={passwordFieldFooterTemplate} />
              )} />
            {getFormErrorMessage('pass')}
          </Col>
        </Row>      
        <Row>
          <Col className='pt-1'>
            <label
              htmlFor="firstName"
              className={classNames({ 'p-error': errors.firstName })}>
              Nombres*
            </label>
            <Controller
              name="firstName"
              control={control}
              rules={{ required: 'Los nombres son requeridos.' }}
              render={({ field, fieldState }) => (
                <InputText
                  id={field.name}
                  {...field}
                  autoFocus
                  maxLength={255}
                  className={classNames({ 'p-invalid': fieldState.invalid })} />
              )} />
            {getFormErrorMessage('firstName')}
          </Col>
        </Row>
        <Row>
          <Col className='pt-1'>
            <label
              htmlFor="lastName"
              className={classNames({ 'p-error': errors.lastName })}>
              Apellidos*
            </label>
            <Controller
              name="lastName"
              control={control}
              rules={{ required: 'Los apellidos son requeridos.' }}
              render={({ field, fieldState }) => (
                <InputText
                  id={field.name}
                  {...field}
                  autoFocus
                  maxLength={255}
                  className={classNames({ 'p-invalid': fieldState.invalid })} />
              )} />
            {getFormErrorMessage('lastName')}
          </Col>
        </Row>
        <Row>
          <Col className='pt-1'>
            <label
              htmlFor="cellPhone"
              className={classNames({ 'p-error': errors.cellPhone })}>
              Celular*
            </label>
            <Controller
              name="cellPhone"
              control={control}
              rules={{ required: 'El celular es requerido.' }}
              render={({ field, fieldState }) => (
                <InputText
                  id={field.name}
                  {...field}
                  autoFocus
                  maxLength={255}
                  className={classNames({ 'p-invalid': fieldState.invalid })} />
              )} />
            {getFormErrorMessage('cellPhone')}
          </Col>
        </Row>
        <Row>
          <Col className='pt-1'>
            <label
              htmlFor="profile"
              className={classNames({ 'p-error': errors.profile })}>
              Perfíl*
            </label>
            <Controller
              name="profile"
              control={control}
              rules={{ required: 'El perfíl es requerido.' }}            
              render={({ field }) => (
                <Dropdown
                  id={field.name}
                  value={field.value}
                  onChange={(e) => field.onChange(e.value)}
                  options={profiles}
                  optionLabel="description" />
              )} />
            {getFormErrorMessage('profile')}
          </Col>
        </Row>
        <Row>
          <Col xs={6} className='pt-1'>
            <label
              htmlFor="active">
              Activo
            </label>
            <br></br>
            <InputSwitch checked={active} onChange={(e) => setActive(e.value)} />
          </Col>
        </Row>
        <Button
          type="submit"
          label="Guardar"
          className="p-button-raised p-button-sm mt-4" />
      </form>
    </>
  );

  const dialogTemplate = (
    <>
      <Dialog
        header=""
        visible={props.visible}
        onHide={onDialogHide}
        breakpoints={{ '960px': '75vw' }}
        style={{ width: '50vw' }}>
        <h1>Usuario</h1>
        {formTemplate}
      </Dialog>
    </>
  );

  // #endregion

  useEffect(() => {
    resetForm();
    getProfiles();
    if (props.user) {
      setIsNew(false);
      reset(props.user);
      if (props.user) {
        setActive(props.user.active);        
      }          
    } else {
      setIsNew(true);  
      setActive(true);      
    }
  }, [props.visible, props.user]);

  return (
    <>
      <article>
        {dialogTemplate}
      </article>
    </>
  );
}

export default UserEditDialog;